"use client";

import { useTranslator } from "~/i18n/provider";
import { discountPercent, formatMoney, isOnSale } from "~/lib/money";
import { cn } from "~/lib/utils";
import type {
  SaleBadgeStyle,
  TaxMode,
} from "~/server/api/schemas/brand-settings";

export interface PriceBlockProps {
  priceMinor: number | null;
  compareAtMinor: number | null;
  currency: string;
  taxMode: TaxMode;
  /**
   * P11-13 · The brand's own word for its tax ("VAT", "Sales tax", "GST").
   * Optional with a "VAT" fallback only so existing tests and callers keep
   * compiling; the PDP passes `settings.taxDisplayLabel`.
   */
  taxLabel?: string;
  /**
   * P11-13 · False when the shop charges no tax anywhere, in which case the
   * note is omitted rather than claiming a VAT that is not in the price.
   * Defaults true so every existing caller is unchanged.
   */
  showTaxNote?: boolean;
  from?: boolean;
  saleBadgeStyle: SaleBadgeStyle;
  showSavingsPercent: boolean;
}

const SALE_BADGE_CLASSES: Record<SaleBadgeStyle, string> = {
  ACCENT: "bg-accent text-accent-fg",
  DANGER: "bg-danger text-danger-fg",
  OUTLINE: "border border-border bg-surface text-fg",
};

export function PriceBlock({
  priceMinor,
  compareAtMinor,
  currency,
  taxMode,
  taxLabel = "VAT",
  showTaxNote = true,
  from = false,
  saleBadgeStyle,
  showSavingsPercent,
}: PriceBlockProps) {
  // Degrades to English outside a provider (see `useTranslator`), which is why
  // it is safe to call before the early return.
  const t = useTranslator();

  if (priceMinor === null) {
    return (
      <div aria-live="polite" aria-atomic="true" data-testid="pdp-price">
        <p className="text-sm text-fg-muted">{t("pdp.priceUnavailable")}</p>
      </div>
    );
  }

  // Formats with the same default locale as the PLP <PriceDisplay> so the two
  // surfaces never diverge (e.g. "US$24.00" on both). Per-brand locale is an
  // i18n concern threaded across every money surface at once (P8), not a
  // PDP-only prop.
  const price = formatMoney(priceMinor, currency);
  const sale = isOnSale(priceMinor, compareAtMinor);
  const compareAt = sale ? formatMoney(compareAtMinor!, currency) : null;
  const savings = discountPercent(priceMinor, compareAtMinor);
  const currentLabel = from ? `From ${price}` : price;
  const spokenPrice = compareAt
    ? `${currentLabel}, was ${compareAt}`
    : currentLabel;

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      data-testid="pdp-price"
      className="flex flex-col gap-1.5"
    >
      <span className="sr-only">{spokenPrice}</span>
      <div aria-hidden className="flex flex-wrap items-center gap-2.5">
        <span className="font-heading text-xl font-semibold tabular-nums text-fg">
          {currentLabel}
        </span>
        {compareAt && (
          <s className="text-sm tabular-nums text-fg-muted">{compareAt}</s>
        )}
        {sale && (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
              SALE_BADGE_CLASSES[saleBadgeStyle],
            )}
          >
            {showSavingsPercent && savings !== null
              ? `Save ${savings}%`
              : "Sale"}
          </span>
        )}
      </div>
      {/* P11-13 · The brand's label, translated — not a hardcoded "VAT" — and
          absent entirely when no tax is charged. */}
      {showTaxNote ? (
        <p className="text-xs text-fg-muted">
          {t(
            taxMode === "INCLUSIVE"
              ? "common.taxIncludedShort"
              : "common.taxExcludedShort",
            { label: taxLabel },
          )}
        </p>
      ) : null}
    </div>
  );
}
